import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getPurchaseHistory, deletePurchases } from "../../api/Calling";
import { toast } from "react-toastify";
import formatDateTime from "../../components/formatDateTime";
import {
  FaShoppingCart,
  FaTrash,
  FaExclamationTriangle,
} from "react-icons/fa";
import { MdClose } from "react-icons/md";

const calculateDateRange = (filters) => {
  const today = new Date();
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  let startDate = "";
  let endDate = formatDate(today);

  switch (filters.dateRangeType) {
    case "today":
      startDate = formatDate(today);
      break;
    case "yesterday":
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      startDate = formatDate(yesterday);
      endDate = formatDate(yesterday);
      break;
    case "last15":
      const last15 = new Date(today);
      last15.setDate(last15.getDate() - 15);
      startDate = formatDate(last15);
      break;
    case "last30":
      const last30 = new Date(today);
      last30.setDate(last30.getDate() - 30);
      startDate = formatDate(last30);
      break;
    case "last6months":
      const last6months = new Date(today);
      last6months.setMonth(last6months.getMonth() - 6);
      startDate = formatDate(last6months);
      break;
    case "custom":
      startDate = filters.customStartDate;
      endDate = filters.customEndDate;
      break;
    default:
      startDate = formatDate(today);
  }

  return { startDate, endDate };
};

const Purchases = () => {
  const navigate = useNavigate();

  const [purchasesData, setPurchasesData] = useState([]);
  const [purchasesLoading, setPurchasesLoading] = useState(false);
  const [purchasesSearch, setPurchasesSearch] = useState("");
  const [purchasesPagination, setPurchasesPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
  });
  const [purchasesSelectedIds, setPurchasesSelectedIds] = useState([]);
  const [filterPurchaseType, setFilterPurchaseType] = useState("all");
  const [purchaseStartDate, setPurchaseStartDate] = useState("");
  const [purchaseEndDate, setPurchaseEndDate] = useState("");
  const [purchaseSortOrder, setPurchaseSortOrder] = useState("desc");
  const [purchaseDateRangeType, setPurchaseDateRangeType] = useState("today");

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);

  const fetchPurchases = async () => {
    setPurchasesLoading(true);
    try {
      let params = `?page=${purchasesPagination.page}&limit=${purchasesPagination.limit}`;

      if (purchasesSearch) params += `&search=${purchasesSearch}`;
      if (filterPurchaseType !== "all") params += `&p_type=${filterPurchaseType}`;

      const { startDate, endDate } = calculateDateRange({
        dateRangeType: purchaseDateRangeType,
        customStartDate: purchaseStartDate,
        customEndDate: purchaseEndDate,
      });

      if (startDate) params += `&startDate=${startDate}`;
      if (endDate) params += `&endDate=${endDate}`;
      if (purchaseSortOrder) params += `&sort=${purchaseSortOrder}`;

      const resp = await getPurchaseHistory(params);

      if (resp?.status && Array.isArray(resp.data)) {
        setPurchasesData(resp.data);
        setPurchasesPagination({
          page: resp.currentPage,
          limit: resp.limit,
          total: resp.total,
          totalPages: resp.totalPages,
        });
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load purchases");
    }
    setPurchasesLoading(false);
  };

  useEffect(() => {
    fetchPurchases();
  }, [purchasesPagination.page]);

  const handleApplyFilters = () => {
    if (purchasesPagination.page === 1) {
      fetchPurchases();
    } else {
      setPurchasesPagination((prev) => ({ ...prev, page: 1 }));
    }
  };

  const openDeleteConfirmation = (ids) => {
    setDeleteTarget(ids);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      const resp = await deletePurchases({ ids: deleteTarget });
      if (resp?.status) {
        toast.success(resp.message || "Deleted successfully");
        setPurchasesSelectedIds([]);
        fetchPurchases();
      } else {
        toast.error(resp?.message || "Delete failed");
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong");
    } finally {
      setShowDeleteModal(false);
      setDeleteTarget(null);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setPurchasesSelectedIds(purchasesData.map((item) => item._id));
    } else {
      setPurchasesSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    if (purchasesSelectedIds.includes(id)) {
      setPurchasesSelectedIds(purchasesSelectedIds.filter((item) => item !== id));
    } else {
      setPurchasesSelectedIds([...purchasesSelectedIds, id]);
    }
  };

  const successfulPurchases = purchasesData.filter((p) => p.p_status === "success").length;
  const pidPurchases = purchasesData.filter((p) => p.p_type === "pid").length;
  const organicPurchases = purchasesData.filter((p) => p.p_type === "organic").length;

  return (
    <>
      {/* HEADER */}
      <div className="mb-10">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-6 mb-8">
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-main-blue rounded-xl shadow-lg">
                <FaShoppingCart className="text-2xl text-main-white" />
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold text-main-font tracking-tight">
                Purchase History
              </h1>
            </div>
            <p className="text-main-subfont text-base lg:text-lg font-medium ml-16">
              View and manage all purchases
            </p>
          </div>
        </div>

        {/* STATS CARDS */}
        <div className="grid grid-cols-1 md:grid-cols-1 gap-4 mb-8">
          <div className="stat-card text-main-white p-6 rounded-2xl shadow-lg relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-main-white/10 rounded-full -mr-8 -mt-8"></div>
            <p className="text-sm font-medium opacity-90 mb-1 relative z-10">Total Purchases</p>
            <p className="text-4xl font-bold relative z-10">{purchasesPagination.total}</p>
          </div>
        </div>

        {/* FILTERS & SEARCH */}
        <div className="bg-main-white rounded-2xl shadow-lg border border-main-border p-6 mb-6">
          <div className="flex flex-col gap-4">
            <div className="w-full">
              <input
                type="text"
                placeholder="Search user, order ID, device, PID..."
                value={purchasesSearch}
                onChange={(e) => setPurchasesSearch(e.target.value)}
                className="mono w-full px-4 py-3 border-2 border-main-border rounded-xl text-base
                           focus:outline-none focus:border-main-blue focus:ring-4 focus:ring-main-blue/10 
                           transition-all duration-200"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <select
                value={purchaseDateRangeType}
                onChange={(e) => setPurchaseDateRangeType(e.target.value)}
                className="w-full px-4 py-3 border-2 border-main-border rounded-xl text-base font-semibold
                           focus:outline-none focus:border-main-blue focus:ring-4 focus:ring-main-blue/10 
                           transition-all duration-200"
              >
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="last15">Last 15 Days</option>
                <option value="last30">Last 30 Days</option>
                <option value="last6months">Last 6 Months</option>
                <option value="custom">Custom Range</option>
              </select>

              <select
                value={filterPurchaseType}
                onChange={(e) => setFilterPurchaseType(e.target.value)}
                className="w-full px-4 py-3 border-2 border-main-border rounded-xl text-base font-semibold
                           focus:outline-none focus:border-main-blue focus:ring-4 focus:ring-main-blue/10 
                           transition-all duration-200"
              >
                <option value="all">All Types</option>
                <option value="pid">PID Only</option>
                <option value="organic">Organic Only</option>
              </select>

              <select
                value={purchaseSortOrder}
                onChange={(e) => setPurchaseSortOrder(e.target.value)}
                className="w-full px-4 py-3 border-2 border-main-border rounded-xl text-base font-semibold
                           focus:outline-none focus:border-main-blue focus:ring-4 focus:ring-main-blue/10 
                           transition-all duration-200"
              >
                <option value="desc">Newest First</option>
                <option value="asc">Oldest First</option>
              </select>

              <div className="hidden lg:block opacity-0 pointer-events-none"></div>

              <button
                onClick={handleApplyFilters}
                className="w-full h-full bg-main-blue text-main-white font-bold text-base rounded-xl
                           hover:bg-main-blue/90 transition-all duration-200 shadow-md py-3"
              >
                Apply
              </button>
            </div>

            {purchaseDateRangeType === "custom" && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mt-2">
                <input
                  type="date"
                  value={purchaseStartDate}
                  onChange={(e) => setPurchaseStartDate(e.target.value)}
                  className="lg:col-span-1 w-full px-4 py-3 border-2 border-main-border rounded-xl text-base
                             focus:outline-none focus:border-main-blue focus:ring-4 focus:ring-main-blue/10 
                             transition-all duration-200"
                />
                <input
                  type="date"
                  value={purchaseEndDate}
                  onChange={(e) => setPurchaseEndDate(e.target.value)}
                  className="lg:col-span-1 w-full px-4 py-3 border-2 border-main-border rounded-xl text-base
                             focus:outline-none focus:border-main-blue focus:ring-4 focus:ring-main-blue/10 
                             transition-all duration-200"
                />
              </div>
            )}
          </div>

          {purchasesSelectedIds.length > 0 && (
            <div className="mt-4 flex items-center justify-between p-4 bg-main-blue/10 rounded-xl border-2 border-main-blue/20">
              <span className="text-main-font font-semibold">
                {purchasesSelectedIds.length} purchase(s) selected
              </span>
              <button
                onClick={() => openDeleteConfirmation(purchasesSelectedIds)}
                className="bg-main-delete text-main-white px-6 py-2 rounded-xl font-semibold 
                           hover:bg-main-delete/90 transition-all duration-200 flex items-center gap-2"
              >
                <FaTrash />
                Delete Selected
              </button>
            </div>
          )}
        </div>

        {/* TABLE */}
        <div className="bg-main-white rounded-3xl shadow-2xl border border-main-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-main-th border-b-2 border-main-border">
                  <th className="p-5 text-center w-16">
                    <input
                      type="checkbox"
                      checked={purchasesSelectedIds.length === purchasesData.length && purchasesData.length > 0}
                      onChange={handleSelectAll}
                      className="w-5 h-5 cursor-pointer"
                    />
                  </th>
                  {["#", "User", "Product Name", "Order ID", "Device", "PID", "Campaign", "Amount", "Type", "Status", "Created At", "Actions"].map((col) => (
                    <th key={col} className="p-5 text-left">
                      <span className="text-main-font font-bold text-sm uppercase tracking-wide">{col}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {purchasesLoading ? (
                  <tr>
                    <td colSpan="13" className="p-16">
                      <div className="flex flex-col items-center justify-center gap-4">
                        <span className="w-12 h-12 border-4 border-main-blue/30 border-t-main-blue rounded-full animate-spin"></span>
                        <p className="text-main-subfont font-medium animate-pulse">Loading...</p>
                      </div>
                    </td>
                  </tr>
                ) : purchasesData.length === 0 ? (
                  <tr>
                    <td colSpan="13" className="p-16">
                      <div className="flex flex-col items-center gap-4">
                        <div className="p-6 bg-main-bg rounded-full">
                          <FaShoppingCart className="text-6xl text-main-subfont/30" />
                        </div>
                        <div className="text-center space-y-2">
                          <p className="text-main-font font-semibold text-xl">No purchases found</p>
                          <p className="text-main-subfont text-base max-w-md">
                            {purchasesSearch || filterPurchaseType !== "all"
                              ? "Try adjusting your filters"
                              : "No purchases recorded yet"}
                          </p>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  purchasesData.map((row, index) => (
                    <tr
                      key={row._id}
                      className="border-b border-main-border/50 hover:bg-main-thHover transition-all duration-200 cursor-pointer"
                      onClick={(e) => {
                        if (!e.target.closest('input[type="checkbox"]') && !e.target.closest("button")) {
                          setSelectedPurchase(row);
                          setShowPurchaseModal(true);
                        }
                      }}
                    >
                      <td className="p-5 text-center">
                        <input
                          type="checkbox"
                          checked={purchasesSelectedIds.includes(row._id)}
                          onChange={() => handleSelectOne(row._id)}
                          className="w-5 h-5 cursor-pointer"
                        />
                      </td>
                      <td className="p-5">
                        <span className="text-main-font font-semibold text-base mono">
                          {(purchasesPagination.page - 1) * purchasesPagination.limit + index + 1}
                        </span>
                      </td>
                      <td className="p-5">
                        <div className="flex items-center gap-3">
                          {row.u_id?.u_profile && (
                            <img src={row.u_id.u_profile} alt={row.u_id.u_name} className="w-10 h-10 rounded-full object-cover" />
                          )}
                          <div className="space-y-1">
                            <div className="text-main-font font-semibold text-base">{row.u_id?.u_name || "N/A"}</div>
                            <div className="text-main-subfont text-sm">{row.u_id?.u_email || row.u_email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="p-5">
                        <span className="text-main-font font-medium text-sm">{row.p_name || "N/A"}</span>
                      </td>
                      <td className="p-5">
                        <span className="mono text-main-font font-semibold text-sm px-3 py-1.5 bg-main-bg rounded-lg">
                          {row.p_order_id}
                        </span>
                      </td>
                      <td className="p-5">
                        <span className="mono text-main-subfont text-sm">{row.p_device}</span>
                      </td>
                      <td className="p-5">
                        <span className="mono text-main-font font-semibold text-sm">{row.p_pid || "N/A"}</span>
                      </td>
                      <td className="p-5">
                        <span className="text-main-font font-medium text-sm">{row.p_campaign || "N/A"}</span>
                      </td>
                      <td className="p-5">
                        <div className="text-center">
                          <div className="text-main-font font-bold text-base">{row.p_amount}</div>
                          <div className="text-main-subfont text-xs">{row.p_currency}</div>
                        </div>
                      </td>
                      <td className="p-5">
                        <div className="flex justify-center">
                          <span className={`px-4 py-2 font-semibold text-sm rounded-xl ${row.p_type === "pid" ? "bg-amber-100 text-amber-700" : "bg-purple-100 text-purple-700"
                            }`}>
                            {row.p_type}
                          </span>
                        </div>
                      </td>
                      <td className="p-5">
                        <div className="flex justify-center">
                          <span className={`px-4 py-2 font-semibold text-sm rounded-xl ${row.p_status === "success" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                            }`}>
                            {row.p_status}
                          </span>
                        </div>
                      </td>
                      <td className="p-5">
                        <div className="text-center space-y-1">
                          <div className="text-main-font text-sm font-semibold mono">
                            {formatDateTime(row.createdAt, "DD/MM/YYYY")}
                          </div>
                          <div className="text-main-subfont text-xs mono">
                            {formatDateTime(row.createdAt, "hh:mm:ss A")}
                          </div>
                        </div>
                      </td>
                      <td className="p-5">
                        <div className="flex justify-center">
                          <button
                            onClick={(e) => { e.stopPropagation(); openDeleteConfirmation([row._id]); }}
                            className="text-main-delete font-semibold hover:bg-main-delete/10 px-5 py-2.5 rounded-xl transition-all duration-200 flex items-center gap-2 border-2 border-transparent hover:border-main-delete/20"
                          >
                            <FaTrash className="text-xs" />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* PAGINATION */}
          {purchasesPagination.totalPages > 1 && (
            <div className="p-6 border-t-2 border-main-border flex justify-between items-center">
              <span className="text-main-subfont text-sm font-medium">
                Showing {(purchasesPagination.page - 1) * purchasesPagination.limit + 1} to{" "}
                {Math.min(purchasesPagination.page * purchasesPagination.limit, purchasesPagination.total)} of{" "}
                {purchasesPagination.total} entries
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPurchasesPagination({ ...purchasesPagination, page: purchasesPagination.page - 1 })}
                  disabled={purchasesPagination.page === 1}
                  className="px-4 py-2 rounded-xl border-2 border-main-border font-semibold text-sm
                             disabled:opacity-40 disabled:cursor-not-allowed hover:bg-main-blue hover:text-main-white
                             hover:border-main-blue transition-all duration-200"
                >
                  Previous
                </button>
                <div className="flex gap-1">
                  {[...Array(purchasesPagination.totalPages)].map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setPurchasesPagination({ ...purchasesPagination, page: i + 1 })}
                      className={`w-10 h-10 rounded-xl font-bold text-sm transition-all duration-200 ${purchasesPagination.page === i + 1
                        ? "bg-main-blue text-main-white"
                        : "border-2 border-main-border hover:bg-main-blue/10"
                        }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setPurchasesPagination({ ...purchasesPagination, page: purchasesPagination.page + 1 })}
                  disabled={purchasesPagination.page === purchasesPagination.totalPages}
                  className="px-4 py-2 rounded-xl border-2 border-main-border font-semibold text-sm
                             disabled:opacity-40 disabled:cursor-not-allowed hover:bg-main-blue hover:text-main-white
                             hover:border-main-blue transition-all duration-200"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* PURCHASE DETAILS MODAL */}
      {showPurchaseModal && selectedPurchase && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-main-white w-full max-w-3xl rounded-3xl shadow-2xl border-2 border-main-border overflow-hidden">
            <div className="bg-gradient-to-r from-main-blue to-main-blue/90 p-8 text-main-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-main-white/10 rounded-full -mr-32 -mt-32"></div>
              <div className="relative z-10 flex justify-between items-start">
                <div className="space-y-2">
                  <h2 className="text-3xl font-bold">Purchase Details</h2>
                  <p className="text-main-white/80 text-sm">Complete purchase information</p>
                </div>
                <button
                  onClick={() => { setShowPurchaseModal(false); setSelectedPurchase(null); }}
                  className="p-2 hover:bg-main-white/20 rounded-xl transition-all duration-200"
                >
                  <MdClose className="text-2xl" />
                </button>
              </div>
            </div>

            <div className="p-8 space-y-6">
              <div className="flex items-center gap-4 pb-6 border-b-2 border-main-border">
                {selectedPurchase.u_id?.u_profile && (
                  <img
                    src={selectedPurchase.u_id.u_profile}
                    alt={selectedPurchase.u_id.u_name}
                    className="w-16 h-16 rounded-full object-cover border-4 border-main-blue/20 cursor-pointer hover:border-main-blue transition-all"
                    onClick={() => navigate(`/admin/manage/user/info/${selectedPurchase.u_id._id}`)}
                  />
                )}
                <div className="flex-1">
                  <h3
                    className="text-xl font-bold text-main-font cursor-pointer hover:text-main-blue transition-all"
                    onClick={() => navigate(`/admin/manage/user/info/${selectedPurchase.u_id?._id}`)}
                  >
                    {selectedPurchase.u_id?.u_name || "N/A"}
                  </h3>
                  <p className="text-main-subfont">{selectedPurchase.u_id?.u_email || selectedPurchase.u_email}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                {[
                  { label: "Product Name", value: selectedPurchase.p_name || "N/A" },
                  { label: "Order ID", value: selectedPurchase.p_order_id, mono: true },
                  { label: "Device", value: selectedPurchase.p_device, mono: true },
                  { label: "PID", value: selectedPurchase.p_pid || "N/A", mono: true },
                  { label: "Campaign", value: selectedPurchase.p_campaign || "N/A" },
                ].map(({ label, value, mono }) => (
                  <div key={label}>
                    <p className="text-sm font-bold text-main-subfont uppercase tracking-wide mb-2">{label}</p>
                    <p className={`text-main-font font-semibold text-lg ${mono ? "mono" : ""}`}>{value}</p>
                  </div>
                ))}

                <div>
                  <p className="text-sm font-bold text-main-subfont uppercase tracking-wide mb-2">Amount</p>
                  <p className="text-main-font font-bold text-2xl">{selectedPurchase.p_amount}</p>
                  <p className="text-main-subfont text-sm">{selectedPurchase.p_currency}</p>
                </div>

                <div>
                  <p className="text-sm font-bold text-main-subfont uppercase tracking-wide mb-2">Status</p>
                  <span className={`inline-flex px-4 py-2 font-semibold text-sm rounded-xl ${selectedPurchase.p_status === "success" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                    }`}>
                    {selectedPurchase.p_status}
                  </span>
                </div>

                <div>
                  <p className="text-sm font-bold text-main-subfont uppercase tracking-wide mb-2">Type</p>
                  <span className={`inline-flex px-4 py-2 font-semibold text-sm rounded-xl ${selectedPurchase.p_type === "pid" ? "bg-amber-100 text-amber-700" : "bg-purple-100 text-purple-700"
                    }`}>
                    {selectedPurchase.p_type}
                  </span>
                </div>

                <div>
                  <p className="text-sm font-bold text-main-subfont uppercase tracking-wide mb-2">Created At</p>
                  <p className="text-main-font font-semibold mono">{formatDateTime(selectedPurchase.createdAt, "DD/MM/YYYY")}</p>
                  <p className="text-main-subfont text-sm mono">{formatDateTime(selectedPurchase.createdAt, "hh:mm:ss A")}</p>
                </div>

                <div>
                  <p className="text-sm font-bold text-main-subfont uppercase tracking-wide mb-2">Updated At</p>
                  <p className="text-main-font font-semibold mono">{formatDateTime(selectedPurchase.updatedAt, "DD/MM/YYYY")}</p>
                  <p className="text-main-subfont text-sm mono">{formatDateTime(selectedPurchase.updatedAt, "hh:mm:ss A")}</p>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-4 p-8 bg-main-bg/30 border-t-2 border-main-border">
              <button
                onClick={() => { setShowPurchaseModal(false); setSelectedPurchase(null); }}
                className="bg-main-blue text-main-white px-10 py-3.5 rounded-2xl font-bold 
                           hover:bg-main-blue/90 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE CONFIRMATION MODAL */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-main-white w-full max-w-lg rounded-3xl shadow-2xl border-2 border-main-border overflow-hidden">
            <div className="bg-gradient-to-r from-main-delete to-main-delete/90 p-8 text-main-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-main-white/10 rounded-full -mr-32 -mt-32"></div>
              <div className="relative z-10 flex flex-col items-center text-center gap-4">
                <div className="p-5 bg-main-white/20 rounded-2xl backdrop-blur-sm">
                  <FaExclamationTriangle className="text-5xl" />
                </div>
                <div>
                  <h2 className="text-3xl font-bold mb-2">Confirm Deletion</h2>
                  <p className="text-main-white/90 text-sm">This action cannot be undone</p>
                </div>
              </div>
            </div>
            <div className="p-8">
              <div className="bg-main-bg/50 border-2 border-main-border rounded-2xl p-6 mb-6">
                <p className="text-main-font text-center leading-relaxed">
                  You are about to delete{" "}
                  <span className="mono font-bold text-main-blue">{deleteTarget?.length || 0}</span>{" "}
                  {deleteTarget?.length === 1 ? "purchase" : "purchases"}.
                </p>
              </div>
              <div className="flex gap-4">
                <button
                  onClick={() => { setShowDeleteModal(false); setDeleteTarget(null); }}
                  className="flex-1 px-6 py-4 text-main-subfont font-bold hover:text-main-font 
                             hover:bg-main-bg rounded-2xl transition-all duration-200 border-2 border-main-border"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="flex-1 bg-main-delete text-main-white px-6 py-4 rounded-2xl font-bold 
                             hover:bg-main-delete/90 shadow-lg hover:shadow-xl transition-all duration-300 
                             flex items-center justify-center gap-2"
                >
                  <FaTrash />
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Purchases;